import React, { useEffect, useState } from 'react';
import { Slider as MuiSlider, Grid, Typography, Popper, IconButton } from '@material-ui/core';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import {
  SelectField,
  RadioField,
  PercentageField,
  Button,
  Slider,
} from '@roc/ui';
import { BROKER_POINTS_OPTIONS, debounce, isNil } from '@roc/feature-utils';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import {
  amortizationOptions,
  amortizationTypeTooltip,
} from '@roc/feature-loans';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionActions from '@material-ui/core/AccordionActions';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import { AddCircleOutline, Edit, RemoveCircleOutline } from '@material-ui/icons';
import { RateScenario } from '@roc/feature-types';
import { useBaseStore } from '@roc/feature-app-core';
import { PricerSummaryStore } from '../../stores/singlePropertyPricer/pricerSummaryStore';

// Rate Range Slider Component
const useRateSliderStyles = makeStyles(theme => ({
  root: {
    color: '#00bcd4',
    height: 8,
  },
  thumb: {
    height: 32,
    width: 32,
    backgroundColor: '#ff9800',
    border: '4px solid #fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    marginTop: -12,
    marginLeft: -16,
    '&::before': {
      content: '"%"',
      fontSize: 12,
      fontWeight: 'bold',
      color: '#fff',
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
    },
    '&:focus, &:hover, &$active': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    },
  },
  active: {},
  track: {
    height: 8,
    borderRadius: 4,
  },
  rail: {
    height: 8,
    borderRadius: 4,
    opacity: 1,
    backgroundColor: '#00bcd4',
  },
  mark: {
    backgroundColor: '#fff',
    height: 16,
    width: 16,
    borderRadius: '50%',
    border: '3px solid #00bcd4',
    marginTop: '-4px',
    marginLeft: -8,
  },
  markActive: {
    opacity: 1,
    backgroundColor: '#fff',
  },
  leftBox: {
    flex: '0 0 33%',
  },
  centerBox: {
    flex: '0 0 34%',
  },
  rightBox: {
    flex: '0 0 33%',
  },
  rateLabel: {
    fontSize: 12,
    color: '#666',
  },
  rateValue: {
    fontWeight: 'bold',
    color: theme.palette.primary.dark,
  },
  caption: {
    fontSize: 10,
    color: '#999',
  },
  captionBlock: {
    fontSize: 10,
    color: '#999',
    display: 'block',
  },
}));

export const RateRangeSlider = observer(({ ratesScenarios, value, onChange, setErrorMessage, isEditRateMode, enteredRate }) => {
  const classes = useRateSliderStyles();
  const marks = [];
  const sliderLabels = [];

  // sort buydown keys in descending order to display the lowest rate on the left and highest on the right
  const buydownKeys = ratesScenarios.map(rate => Number(rate.buydownKey)).sort((a, b) => b - a);

  let previousSliderValue = -1;
  buydownKeys.forEach(key => {
    const scenario = ratesScenarios.find(s => Number(s.buydownKey) === key);
    if (scenario.rate) {
      marks.push({ value: scenario.rate, label: '' });
      sliderLabels.push({ value: scenario.rate, label: scenario.rate.toFixed(2) + '%', isError: false });
      previousSliderValue = scenario.rate;
    } else {
      previousSliderValue += 1;
      const errorMessage = scenario.validationError ? scenario.validationError
        : scenario.baseRate?.errorMessage?.length > 0 ? scenario.baseRate.errorMessage[0] : 'No rate available';
      marks.push({ value: previousSliderValue, label: '' });
      sliderLabels.push({ value: previousSliderValue, label: errorMessage, isError: true });
    }
  });

  const minRate = ratesScenarios.find(s => Number(s.buydownKey) === buydownKeys[0])?.rate;
  const midRate = ratesScenarios.find(s => Number(s.buydownKey) === buydownKeys[2])?.rate;
  const maxRate = ratesScenarios.find(s => Number(s.buydownKey) === buydownKeys[4])?.rate;

  setErrorMessage(sliderLabels.find(m => m.value === value && m.isError)?.label);

  return (
    <Box position="relative" width="100%">
      <Box position="relative" px={2} mb={3}>
        <MuiSlider
          classes={classes}
          value={isEditRateMode ? enteredRate : value}
          onChange={onChange}
          marks={marks}
          min={minRate || 0}
          max={maxRate || previousSliderValue}
          step={null}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => {
            return (
              <Box
                textAlign="center"
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  border: '2px solid #00bcd4',
                  color: 'black',
                  width: 'max-content',
                  maxWidth: 300,
                }}
              >
                {isEditRateMode ? `${enteredRate}%` : sliderLabels.find(m => m.value === value)?.label}
              </Box>
            );
          }}
        />
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mt={1}
      >
        <Box textAlign="left" className={classes.leftBox}>
          <Typography variant="body2" className={classes.rateLabel}>
            Low Rate
          </Typography>
          <Typography variant="h6" className={classes.rateValue}>
            {minRate ? `${minRate.toFixed(2)}%` : 'N/A'}
          </Typography>
          <Typography variant="caption" className={classes.caption}>
            Max Buydown
          </Typography>
          <Typography variant="caption" className={classes.captionBlock}>
            (to Capital Provider)
          </Typography>
        </Box>
        <Box textAlign="center" className={classes.centerBox}>
          <Typography variant="body2" className={classes.rateLabel}>
            Mid Rate
          </Typography>
          <Typography variant="h6" className={classes.rateValue}>
            {midRate ? `${midRate.toFixed(2)}%` : 'N/A'}
          </Typography>
        </Box>
        <Box textAlign="right" className={classes.rightBox}>
          <Typography variant="body2" className={classes.rateLabel}>
            High Rate
          </Typography>
          <Typography variant="h6" className={classes.rateValue}>
            {maxRate ? `${maxRate.toFixed(2)}%` : 'N/A'}
          </Typography>
          <Typography variant="caption" className={classes.caption}>
            Min Points/Fees
          </Typography>
          <Typography variant="caption" className={classes.captionBlock}>
            (to Capital Provider)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

// Rate Scenario Card Component
const useRateScenarioCardStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    backgroundColor: '#fff',
    marginBottom: 16,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  label: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
  },
  value: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  subValue: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  editIcon: {
    fontSize: 16,
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  errorMessage: {
    color: theme.palette.error.main,
  },
}));

interface RateScenarioCardProps {
  scenario: RateScenario;
  youMake: number;
  youMakePercent: number;
  onAddToQuote: () => void;
  onEditRate?: () => void;
  isEditRateMode?: boolean;
  hasNAScenario?: boolean;
  store: PricerSummaryStore;
  isBroker?: boolean;
}

export const RateScenarioCard = observer(
  ({
    scenario,
    youMake,
    youMakePercent,
    onAddToQuote,
    onEditRate,
    isEditRateMode,
    hasNAScenario,
    store: pricerSummaryStore,
    isBroker,
  }: RateScenarioCardProps) => {
    const classes = useRateScenarioCardStyles();

    return (
      <Box className={classes.card}>
        <Box className={classes.section}>
          <Typography className={classes.label}>Interest Rate</Typography>
          <Box className={classes.value}>
            {!isEditRateMode && <Typography
              variant="h5"
              component="span"
              style={{ fontWeight: 'bold' }}
            >
              {formatPercentage(scenario.rate)}
            </Typography>}
            {!isEditRateMode && !hasNAScenario && <Edit className={classes.editIcon} onClick={onEditRate} />}
            {isEditRateMode && <PercentageField
              testId='targetRateInput'
              value={pricerSummaryStore.targetRateScenario.rate}
              style={{
                maxWidth: '100px',
                margin: '0px 0px',
                backgroundColor: 'white',
              }}
              variant={'outlined'}
              decimalScale={2}
              allowNegative={false}
              onBlur={() => {
                if (Number(pricerSummaryStore.targetRateScenario.rate) < pricerSummaryStore.minTargetRate) {
                  pricerSummaryStore.setTargetRate(pricerSummaryStore.minTargetRate);
                } else if (Number(pricerSummaryStore.targetRateScenario.rate) > pricerSummaryStore.maxTargetRate) {
                  pricerSummaryStore.setTargetRate(pricerSummaryStore.maxTargetRate);
                }
                pricerSummaryStore.targetRateScenario.minRate = pricerSummaryStore.targetRateScenario.rate;
                pricerSummaryStore.getBuydownForTargetedRate(pricerSummaryStore.targetRateScenario, pricerSummaryStore.targetRateScenario.rate);
              }}
              onChange={(_, value) => {
                pricerSummaryStore.setTargetRate(value);
              }}
            />}
          </Box>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.label}>Points/Fees toward Rate</Typography>
          <Typography className={classes.value}>
            {formatCurrency(scenario.pointsAndFees)}
          </Typography>
          <Typography className={classes.subValue}>
            {formatPercentage(scenario.pointsAndFeesPercent)} of Loan Amount
          </Typography>
        </Box>
        {!isBroker && (
        <Box className={classes.section}>
          <Typography className={classes.label}>You Make</Typography>
          <Typography className={classes.value}>
            {formatCurrency(youMake)}
          </Typography>
          <Typography className={classes.subValue}>
            {formatPercentage(youMakePercent)} of Loan Amount
          </Typography>
        </Box>
        )}
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={onAddToQuote}
            testId="addToQuote"
          >
            Add to Quote
          </Button>
        </Box>
      </Box>
    );
  }
);

export const ErrorMessageCard = observer(({ message }) => {
  const classes = useRateScenarioCardStyles();

  return (
    <Box className={classes.card}>
      <Box className={classes.section}>
        <Typography className={classes.errorMessage}>{message}</Typography>
      </Box>
    </Box>
  );
});

// Field Components
interface AmortizationTypeFieldProps {
  value: any;
  onChange: (value: any) => void;
  className?: string;
  disabled?: boolean;
}

export const AmortizationTypeField = observer(
  ({
    value,
    onChange,
    className,
    disabled = false,
  }: AmortizationTypeFieldProps) => {
    return (
      <Box mb={2} mr={4}>
        <SelectField
          disabled={disabled}
          standaloneLabel
          label={'Amortization Type'}
          name={'amortization'}
          value={value}
          tooltip={amortizationTypeTooltip}
          options={amortizationOptions}
          onChange={onChange}
          variant="outlined"
          testId="amortization"
          className={className}
          fullWidth
        />
      </Box>
    );
  }
);

interface RateTypeFieldProps {
  value: any;
  onChange: (value: any) => void;
  options: any[];
  className?: string;
  disabled?: boolean;
}

export const RateTypeField = observer(
  ({
    value,
    onChange,
    options,
    className,
    disabled = false,
  }: RateTypeFieldProps) => {
    return (
      <Box mb={2} mr={4}>
        <SelectField
          disabled={disabled}
          standaloneLabel
          label={'Rate Type'}
          name={'rateType'}
          value={value}
          options={options}
          onChange={onChange}
          variant="outlined"
          testId="rateType"
          className={className}
          fullWidth
        />
      </Box>
    );
  }
);

interface PrePaymentPenaltyFieldProps {
  value: any;
  onChange: (value: any) => void;
  options: any[];
  className?: string;
  disabled?: boolean;
}

export const PrePaymentPenaltyField = observer(
  ({
    value,
    onChange,
    options,
    className,
    disabled = false,
  }: PrePaymentPenaltyFieldProps) => {
    return (
      <Box mb={2} mr={4}>
        <SelectField
          disabled={disabled}
          standaloneLabel
          label={'Pre-Payment Penalty'}
          name={'prePaymentPenalty'}
          value={value}
          options={options}
          onChange={onChange}
          variant="outlined"
          testId="prePaymentPenalty"
          className={className}
          fullWidth
        />
      </Box>
    );
  }
);

interface LoanToValueFieldProps {
  value: number;
  onChange: (name: string, value: any) => void;
  onBlur?: () => void;
  className?: string;
  disabled?: boolean;
}

export const LoanToValueField = observer(
  ({
    value,
    onChange,
    onBlur,
    className,
    disabled = false,
  }: LoanToValueFieldProps) => {
    return (
      <Box mb={2} mr={4}>
        <Slider
          testId="loanToValueSlider"
          standaloneLabel
          label="Loan To Value"
          disabled={false}
          onChangeCommitted={(event, value) => onChange('loanToValue', value)}
          style={{
            marginTop: '2.5em',
            marginLeft: '16px',
            width: '90%',
          }}
          value={value}
          valueLabelDisplay="on"
          marks={[
            {
              value: 0,
              label: `0%`,
            },
            {
              value: 80,
              label: `80%`,
            },
          ]}
          step={1}
          min={0}
          max={80}
        />
      </Box>
    );
  }
);

const usePercentagePickerStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardLabel: {
    display: 'flex',
    fontSize: '1rem',
    alignItems: 'center',
    marginBottom: '4px',
  },
  iconButton: {
    color: theme.palette.grey[500],
  },
  percentageField: ({ inputFontColor }: any) => ({
    '& .MuiInputBase-input': {
      textAlign: 'right',
      fontSize: '18px!important',
      // fontWeight: 'bold !important',
      // color: inputFontColor,
    },
    '& .MuiTypography-colorTextSecondary': {
      // color: inputFontColor,
      // fontWeight: 'bold',
    },
  }),
}));

export const PercentageWithInputPicker = observer(
  ({
    label,
    value,
    onChange,
    min = 0,
    max = 80,
  }) => {
    const inputFontColor = '#6B6C71';
    const classes = usePercentagePickerStyles({ inputFontColor });

    const handleMinusClick = () => {
      const newValue = Math.max(min, Math.min(max, value - 1));
      onChange(newValue);
    }

    const handlePlusClick = () => {
      const newValue = Math.max(min, Math.min(max, value + 1));
      onChange(newValue);
    }

    const handleChange = (name: string, newValue: any) => {
      if (newValue < min || newValue > max) {
        onChange(value); // Reset to last valid value
        return;
      }
      onChange(newValue);
    }

    return (
      <Box display="flex" flexDirection="column">
        <Typography variant="body2" className={classes.cardLabel}>
          {label}
        </Typography>
        <Box display="flex" alignItems="center">
          <Box width={24} pr={1}>
            {value > min && (
              <IconButton
                className={classes.iconButton}
                onClick={handleMinusClick}
                style={{ padding: 0 }}
              >
                <RemoveCircleOutline />
              </IconButton>
            )}
          </Box>
          <Box width={80} textAlign={'right'}>
            <PercentageField
              testId={`${label}-PercentageField`}
              value={value || 0}
              variant={'standard'}
              fullWidth={true}
              onChange={handleChange}
              inputClassname={classes.percentageField}
              allowNegative={false}
              decimalScale={0}
              limitedPercentage
              min={min}
              max={max}
            />
          </Box>
          <Box width={24} pl={1}>
            {value < max && (
              <IconButton
                className={classes.iconButton}
                onClick={handlePlusClick}
                style={{ padding: 0 }}
              >
                <AddCircleOutline />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
);

interface BrokerPointsFieldProps {
  value: any;
  onChange: (value: any) => void;
  className?: string;
  disabled?: boolean;
}

export const BrokerPointsField = observer(
  ({
    value,
    onChange,
    className,
    disabled = false,
  }: BrokerPointsFieldProps) => {
    return (
      <Grid item xs={6} style={{ alignSelf: 'flex-start' }}>
        <Box mb={2} mr={4} mt={1}>
          <SelectField
            disabled={disabled}
            standaloneLabel
            label={'Broker Points'}
            name={'brokerPointsIn'}
            value={value}
            options={BROKER_POINTS_OPTIONS}
            onChange={onChange}
            variant="outlined"
            fullWidth
            testId="brokerPointsIn"
            subLabel="Enter the points for the third party broker"
            className={className}
          />
        </Box>
      </Grid>
    );
  }
);

interface HudPaymentFieldProps {
  value: any;
  onChange: (value: any) => void;
  noteText: string;
  noteClassName?: string;
  disabled?: boolean;
}

const options = [
  { label: 'On the HUD', value: true },
  { label: 'Off the HUD', value: false },
];

export const HudPaymentField = observer(
  ({
    value,
    onChange,
    noteText,
    noteClassName,
    disabled = false,
  }: HudPaymentFieldProps) => {
    const { globalStore } = useBaseStore();

    if (globalStore.lenderInfo?.isInternal !== true) {
      return null;
    }

    return (
      <Grid item xs={6} style={{ alignSelf: 'flex-start', paddingLeft: '0px' }}>
        <Box mt={1}>
          <RadioField
            standaloneLabel
            label="How do you want the broker to be paid ?"
            value={value}
            options={options}
            onChange={onChange}
            required={true}
            fullWidth
            testId="hud"
            row
          />
          <Typography className={noteClassName}>{noteText}</Typography>
        </Box>
      </Grid>
    );
  }
);

interface TargetRateInputFieldProps {
  value: any;
  onChange: (name: string, value: any) => void;
  onBlur: () => void;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
}

export const TargetRateInputField = observer(
  ({
    value,
    onChange,
    onBlur,
    className,
    inputClassName,
    disabled = false,
  }: TargetRateInputFieldProps) => {
    return (
      <PercentageField
        testId="targetRateInput"
        value={value}
        inputClassname={inputClassName}
        style={{
          maxWidth: '100px',
          margin: '0px 0px',
          backgroundColor: 'white',
        }}
        variant={'outlined'}
        decimalScale={2}
        allowNegative={false}
        onBlur={onBlur}
        onChange={onChange}
      />
    );
  }
);

// Other components
const useAccordionStyles = makeStyles({
  root: {
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    borderBottomWidth: 0,
    marginBottom: '16px',
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: '16px 0',
      borderBottomWidth: 1,
      boxShadow: '0px 0px 16px 0px #757373',
    },
  },
  expanded: {},
});

export const Accordion = (props: any) => {
  const classes = useAccordionStyles();
  return <MuiAccordion classes={classes} {...props} />;
};

const useAccordionSummaryStyles = makeStyles({
  root: {
    backgroundColor: '#fff',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    padding: '4px 16px',
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
      borderBottomWidth: 0,
      background: '#fbfbfb',
    },
  },
  content: {
    margin: '0',
    '&$expanded': {},
  },
  expanded: {},
});

export const AccordionSummary = (props: any) => {
  const classes = useAccordionSummaryStyles();
  return <MuiAccordionSummary classes={classes} {...props} />;
};

const useAccordionActionsStyles = makeStyles({
  root: {
    backgroundColor: '#fbfbfb',
  },
});

export const AccordionActions = (props: any) => {
  const classes = useAccordionActionsStyles();
  return <MuiAccordionActions classes={classes} {...props} />;
};

const useAccordionDetailsStyles = makeStyles({
  root: {
    backgroundColor: '#fbfbfb',
  },
});

export const AccordionDetails = (props: any) => {
  const classes = useAccordionDetailsStyles();
  return <MuiAccordionDetails classes={classes} {...props} />;
};

const useCustomSliderStyles = makeStyles({
  root: {
    color: '#52af77',
    height: 8,
  },
  thumb: {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    marginTop: -8,
    marginLeft: -12,
    '&:focus, &:hover, &$active': {
      boxShadow: 'inherit',
    },
  },
  active: {},
  valueLabel: {
    left: 'calc(-50% + 4px)',
  },
  track: {
    height: 8,
    borderRadius: 4,
  },
  rail: {
    height: 8,
    borderRadius: 4,
  },
});

export const CustomSlider = (props: any) => {
  const classes = useCustomSliderStyles();
  return <MuiSlider classes={classes} {...props} />;
};
